import { ChevronDown, ChevronUp, GripVertical } from "lucide-react";
import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { type CategoryType } from "~/api/types";
import { updateCategoryOrder } from "~/api/v2/requests";
import { Button } from "~/atoms/button";
import { Card, CardContent } from "~/atoms/card";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "~/atoms/dialog";
import { useCategories } from "~/api/swr/useCategories";
import { DragDropProvider, type DragDropEventHandlers } from "@dnd-kit/react";
import { isSortableOperation, useSortable } from "@dnd-kit/react/sortable";
import { moveItemByIndex } from "~/helpers/moveItemByIndex";
import { cn } from "~/lib/utils";
interface Props {
  categories: CategoryType[];
  trigger: React.ReactNode;
  onClosePopover: () => void;
}

type OnDragEnd = NonNullable<DragDropEventHandlers["onDragEnd"]>;

const toSortedCategories = (categories: CategoryType[]) =>
  [...categories].sort((a, b) => a.order - b.order);

export const ReorderCategoryModal = ({
  categories,
  trigger,
  onClosePopover,
}: Props) => {
  const { t } = useTranslation();
  const { mutate: mutateAll } = useCategories();
  const [open, setOpen] = useState(false);
  const [categoriesState, setCategoriesState] = useState(() =>
    toSortedCategories(categories),
  );
  const [animatingId, setAnimatingId] = useState<number | null>(null);
  const maxTitleLen = useMemo(
    () =>
      categoriesState.length > 0
        ? Math.max(...categoriesState.map((category) => category.title.length))
        : 0,
    [categoriesState],
  );

  const bump = (id: number) => {
    setAnimatingId(id);
    setTimeout(() => {
      setAnimatingId(null);
    }, 300);
  };

  const moveCategory = useCallback((categoryId: number, offset: -1 | 1) => {
    setCategoriesState((currentCategories) => {
      const fromIndex = currentCategories.findIndex(
        (category) => category.id === categoryId,
      );
      if (fromIndex < 0) return currentCategories;

      const toIndex = fromIndex + offset;
      if (toIndex < 0 || toIndex >= currentCategories.length) {
        return currentCategories;
      }

      return moveItemByIndex(currentCategories, fromIndex, toIndex);
    });
    bump(categoryId);
  }, []);

  const reorderByIndex = useCallback((fromIndex: number, toIndex: number) => {
    setCategoriesState((currentCategories) => {
      if (
        fromIndex < 0 ||
        toIndex < 0 ||
        fromIndex >= currentCategories.length ||
        toIndex >= currentCategories.length
      ) {
        return currentCategories;
      }

      return moveItemByIndex(currentCategories, fromIndex, toIndex);
    });
  }, []);

  const handleUp = useCallback(
    (categoryId: number) => {
      moveCategory(categoryId, -1);
    },
    [moveCategory],
  );

  const handleDown = useCallback(
    (categoryId: number) => {
      moveCategory(categoryId, 1);
    },
    [moveCategory],
  );

  const handleOpenChange = useCallback(
    (nextOpen: boolean) => {
      setOpen(nextOpen);
      if (nextOpen) {
        setCategoriesState(toSortedCategories(categories));
      }
    },
    [categories],
  );

  const handleDragEndEvent = useCallback<OnDragEnd>(
    (event) => {
      if (event.canceled) return;
      if (!isSortableOperation(event.operation)) return;

      const source = event.operation.source;
      if (!source) return;

      reorderByIndex(source.initialIndex, source.index);
    },
    [reorderByIndex],
  );

  const handleSave = async () => {
    try {
      await updateCategoryOrder(
        categoriesState.map((category, index) => ({
          ...category,
          order: index,
        })),
      );
      void mutateAll();
      toast.success(
        `${t(($) => $.commons.document.categories)} ${t(($) => $.toasts.success.reorder)}`,
      );
      onClosePopover();
    } catch {
      toast.error(t(($) => $.toasts.error.reorder));
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent className="max-w-fit">
        <DialogHeader>
          <DialogTitle>
            {t(($) => $.commons.buttons.reorder)}{" "}
            {t(($) => $.commons.document.categories)}
          </DialogTitle>
        </DialogHeader>
        <DragDropProvider onDragEnd={handleDragEndEvent}>
          {categoriesState.map((category, index) => (
            <ReorderRow
              key={category.id}
              category={category}
              index={index}
              maxTitleLen={maxTitleLen}
              animatingId={animatingId}
              onUp={handleUp}
              onDown={handleDown}
              total={categoriesState.length}
            />
          ))}
        </DragDropProvider>
        <Button onClick={handleSave}>{t(($) => $.commons.buttons.save)}</Button>
      </DialogContent>
    </Dialog>
  );
};

const ReorderRow = ({
  category,
  index,
  maxTitleLen,
  animatingId,
  onUp,
  onDown,
  total,
}: {
  category: CategoryType;
  index: number;
  maxTitleLen: number;
  animatingId: number | null;
  onUp: (categoryId: number) => void;
  onDown: (categoryId: number) => void;
  total: number;
}) => {
  const { ref, isDragging } = useSortable({
    id: `${category.id}`,
    index,
    type: "CATEGORY_ROW",
    accept: "CATEGORY_ROW",
  });

  return (
    <Card
      ref={ref}
      data-bump={animatingId === category.id}
      className={cn(
        "relative transition-all duration-200 data-[bump=true]:scale-[1.03] data-[bump=true]:shadow-lg",
        isDragging &&
          "border-primary/50 bg-primary/5 scale-[0.97] border-2 border-dashed opacity-50",
      )}
    >
      <CardContent className="p-1">
        <span
          className="text-muted-foreground/70 hover:text-muted-foreground absolute top-2 left-2 cursor-grab select-none"
          aria-hidden="true"
          title="Drag"
        >
          <GripVertical className="size-4" />
        </span>
        <div className="flex items-center justify-between py-1">
          <div
            className="text-center text-sm font-medium"
            style={{ width: `${maxTitleLen}ch` }}
          >
            {category.title}
          </div>
          <div className="flex flex-col gap-1">
            <Button
              size="xs"
              variant="ghost"
              disabled={index === 0}
              onClick={() => onUp(category.id)}
            >
              <ChevronUp />
            </Button>
            <Button
              size="xs"
              variant="ghost"
              disabled={index === total - 1}
              onClick={() => onDown(category.id)}
            >
              <ChevronDown />
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};
